import { useLocation } from "react-router-dom";
import { Search, Plus, Bell } from "lucide-react";
import { Avatar, AvatarFallback } from "../../ui/avatar";
import { useDashboardChrome } from "./dashboard-chrome";

// Human-readable title + breadcrumb context for the current location. Keeps the
// owner oriented — they always know where they are.
const TITLES: { match: (p: string) => boolean; section: string; title: string }[] = [
  { match: (p) => p === "/control", section: "Today", title: "Dashboard" },
  { match: (p) => p.startsWith("/control/schedule"), section: "Operations", title: "Schedule" },
  { match: (p) => p.startsWith("/control/bookings"), section: "Operations", title: "Bookings" },
  { match: (p) => p.startsWith("/control/quotes"), section: "Bookings", title: "Quote requests" },
  { match: (p) => p.startsWith("/control/customers"), section: "Operations", title: "Customers" },
  { match: (p) => p.startsWith("/control/services"), section: "Catalogue", title: "Services" },
  { match: (p) => p.startsWith("/control/shop"), section: "Shop", title: "Orders" },
  { match: (p) => p.startsWith("/control/inventory"), section: "Shop", title: "Catalogue" },
  { match: (p) => p.startsWith("/control/cms"), section: "Content", title: "Content" },
  { match: (p) => p.startsWith("/control/settings"), section: "Configuration", title: "Settings" },
];

function useLocationTitle() {
  const { pathname } = useLocation();
  const hit = TITLES.find((t) => t.match(pathname));
  return hit ?? { section: "Charme", title: "Admin" };
}

export function Header() {
  const { openPalette } = useDashboardChrome();
  const { section, title } = useLocationTitle();

  const raw = typeof window !== "undefined" ? localStorage.getItem("user") : null;
  let name = "Admin";
  try {
    if (raw) name = JSON.parse(raw)?.user?.name ?? "Admin";
  } catch {
    /* ignore malformed cache */
  }
  const initials = name
    .split(" ")
    .map((n: string) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 hidden h-20 items-center gap-4 border-b border-[#EAD7D6] bg-[#FFFBFB]/85 px-8 backdrop-blur lg:flex">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">{section}</p>
        <h1 className="truncate font-serif text-2xl leading-tight text-[#3a2e2c]">{title}</h1>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={openPalette}
          className="group flex h-11 items-center gap-2 rounded-full border border-[#EAD7D6] bg-white px-4 text-sm text-[#9A6B69] transition hover:border-[#B87775] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
        >
          <Search className="h-4 w-4" />
          <span className="hidden xl:inline">Search bookings, customers…</span>
          <kbd className="ml-1 hidden rounded border border-[#EAD7D6] bg-[#FBEFEF] px-1.5 py-0.5 font-sans text-[11px] text-[#B87775] xl:inline">
            ⌘K
          </kbd>
        </button>

        <button
          type="button"
          onClick={openPalette}
          aria-label="Quick create"
          className="flex h-11 items-center gap-1.5 rounded-full bg-[#B87775] px-4 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
        >
          <Plus className="h-4 w-4" />
          New
        </button>

        <button
          type="button"
          aria-label="Notifications"
          className="flex h-11 w-11 items-center justify-center rounded-full text-[#9A6B69] transition hover:bg-[#FBEFEF] hover:text-[#B87775] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
        >
          <Bell className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2.5 pl-1">
          <Avatar className="h-9 w-9 bg-[#B87775] text-sm text-white">
            <AvatarFallback className="bg-[#B87775] text-white">{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden text-sm font-medium text-[#3a2e2c] xl:inline">{name}</span>
        </div>
      </div>
    </header>
  );
}
